import React, { type FC } from 'react'
import {
    Avatar,
    Card,
    CardContent,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    LinearProgress,
    Link,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    type ListItemTextProps,
    type SxProps,
    type Theme,
    Typography,
} from '@mui/material'
import { AddShoppingCart } from '@mui/icons-material'

import { Translation } from '../../../../domain'

import { DocumentationType } from '../../../../application'

import { useAsyncCallback } from '../../Callback'
import { Button } from '../../common'
import { SalesEmailLink } from '../../Contact'
import { DocumentationLink } from '../../Documentation'
import { FormattedDateTime, FormattedTranslation, useTranslator } from '../../Internationalization'
import { useAppUsecase } from '../../State'
import { isAddOrUpdateModalOpen } from '../AddOrUpdate'
import { useApplication } from './Hooks'
import { ServiceCardMedia } from './Media'

const headerStyle: SxProps<Theme> = { display: 'flex', mb: 2 }
const applicationIconStyle: SxProps<Theme> = {
    maxWidth: '150px',
    m: 3,
    objectFit: 'scale-down',
    objectPosition: 'top',
    '& > svg': { height: '100%', width: '150px' },
}
const cardInfoStyle: SxProps<Theme> = {
    display: 'flex',
    flexDirection: 'column',
    '& > *': { display: '-webkit-inline-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', wordBreak: 'break-word', overflow: 'hidden' },
}

const muiListItemTextSecondaryStyle: SxProps<Theme> = {
    display: '-webkit-inline-box',
    WebkitLineClamp: 10,
    WebkitBoxOrient: 'vertical',
    wordBreak: 'break-word',
    overflow: 'hidden',
}
const avatarStyle: SxProps<Theme> = { backgroundColor: 'secondary.main' }

const ItemText: FC<Pick<ListItemTextProps, 'primary' | 'secondary' | 'secondaryTypographyProps'>> = (props) => (
    <ListItemText {...props} primaryTypographyProps={{ variant: 'body1' }} />
)

export const ApplicationDialog: FC = () => {
    const translator = useTranslator()

    const installUsecase = useAppUsecase('installServiceCatalogApplicationUsecase')

    const application = useApplication()
    const isOpen = isAddOrUpdateModalOpen()

    const [start, loading] = useAsyncCallback(() => installUsecase.start(), [installUsecase])

    if (application === null || isOpen) {
        return <></>
    }

    const close: VoidFunction = () => installUsecase.close()

    return (
        <Dialog data-testid="service-catalog-application-dialog" fullWidth disableEscapeKeyDown onClose={close} maxWidth="sm" open>
            <DialogTitle>
                <FormattedTranslation id={Translation.SERVICE_DETAILS} />
            </DialogTitle>
            <LinearProgress variant="determinate" value={0} />
            <DialogContent>
                <Card sx={headerStyle}>
                    <ServiceCardMedia sx={applicationIconStyle} icon={application.icon} name={application.name} />
                    <CardContent sx={cardInfoStyle}>
                        <Typography gutterBottom variant="h4">
                            {application.name}
                        </Typography>
                        <Typography variant="body1" color="textSecondary">
                            {application.provider}
                        </Typography>
                    </CardContent>
                </Card>
                <Grid container>
                    <Grid size={{ xs: 9 }}>
                        <List>
                            <ListItem>
                                <ItemText
                                    primary={<FormattedTranslation id={Translation.DESCRIPTION} />}
                                    secondary={application.description}
                                    secondaryTypographyProps={{ sx: muiListItemTextSecondaryStyle, variant: 'body2' }}
                                />
                            </ListItem>
                        </List>
                    </Grid>
                    <Grid size={{ xs: 3 }}>
                        <List>
                            <ListItem>
                                <ItemText
                                    primary={<FormattedTranslation id={Translation.DOCUMENTATION} />}
                                    secondary={
                                        <DocumentationLink type={DocumentationType.SERVICES_COMMISSIONING_FILE}>
                                            <FormattedTranslation id={Translation.CYBUS_DOCS} />
                                        </DocumentationLink>
                                    }
                                    secondaryTypographyProps={{ variant: 'body2' }}
                                />
                            </ListItem>
                        </List>
                    </Grid>
                </Grid>
                <Typography gutterBottom variant="subtitle1">
                    <FormattedTranslation id={Translation.ADDITIONAL_INFO} />
                </Typography>
                <Grid container>
                    <Grid size={{ xs: 9 }}>
                        <List>
                            <ListItem>
                                <ItemText
                                    primary={<FormattedTranslation id={Translation.REQUIRED_PERMISSION} />}
                                    secondary={application.directory}
                                    secondaryTypographyProps={{ variant: 'body2' }}
                                />
                                {!application.available && (
                                    <ListItemAvatar>
                                        <Avatar sx={avatarStyle}>
                                            <AddShoppingCart />
                                        </Avatar>
                                    </ListItemAvatar>
                                )}
                            </ListItem>
                            <ListItem>
                                <ItemText
                                    primary={<FormattedTranslation id={Translation.HOMEPAGE} />}
                                    secondary={
                                        <Link href={application.homepage} target="_blank">
                                            {application.homepage}
                                        </Link>
                                    }
                                    secondaryTypographyProps={{ variant: 'body2' }}
                                />
                            </ListItem>
                        </List>
                    </Grid>
                    <Grid size={{ xs: 3 }}>
                        <List>
                            <ListItem>
                                <ItemText
                                    primary={<FormattedTranslation id={Translation.VERSION} />}
                                    secondary={application.version}
                                    secondaryTypographyProps={{ variant: 'body2' }}
                                />
                            </ListItem>
                            <ListItem>
                                <ItemText
                                    primary={<FormattedTranslation id={Translation.LAST_UPDATED} />}
                                    secondary={<FormattedDateTime date={application.updatedAt} format="dateOnly" />}
                                    secondaryTypographyProps={{ variant: 'body2' }}
                                />
                            </ListItem>
                        </List>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button data-testid="service-catalog-application-dialog-cancel" variant="secondary" onClick={close}>
                    <FormattedTranslation id={Translation.CLOSE} />
                </Button>
                {application.available ? (
                    <Button data-testid="service-catalog-application-dialog-install" variant="primary" disabled={loading} onClick={start}>
                        {loading ? <CircularProgress size="1.5rem" color="inherit" /> : <FormattedTranslation id={Translation.INSTALL} />}
                    </Button>
                ) : (
                    <SalesEmailLink
                        data-testid="service-catalog-application-dialog-request"
                        variant="button"
                        color="secondary"
                        subject={translator.formatTranslation(Translation.REQUEST_SERVICE_SUBJECT, application)}
                    >
                        <FormattedTranslation id={Translation.REQUEST_SERVICE} />
                    </SalesEmailLink>
                )}
            </DialogActions>
        </Dialog>
    )
}
